import { Flex } from '@sequencehq/core-components'
import { ConfigurationManagementProps } from 'modules/Integrations/domain'
import invariant from 'tiny-invariant'
import { useState, useCallback, useMemo } from 'react'
import { RilletIntegrationConfig } from 'Integrations/integrationsConfig/rillet/rillet.integration.config'
import RilletAuthenticationCard from './cards/RilletAuthenticationCard'
import { AutoLinkCustomersCard } from 'Integrations/integrationsConfig/common/AutoLinkCustomers/AutoLinkCustomersCard'

export const RilletConfigurationManagement = ({
  updateValidationState,
  updateConfiguration,
  integration
}: ConfigurationManagementProps) => {
  invariant(integration.id === 'Rillet', 'Integration is not Rillet')

  const rilletConfiguration = useMemo(
    () => integration.configuration as RilletIntegrationConfig,
    [integration.configuration]
  )

  const [wasConnectionSuccessful, setWasConnectionSuccessful] = useState(false)

  const [config, setConfig] =
    useState<RilletIntegrationConfig>(rilletConfiguration)

  // Forward synchronously rather than from an effect: updateConfiguration's
  // identity changes whenever it writes to the editor, so an effect keyed on it
  // would loop.
  const updateConfig = useCallback(
    (patch: Partial<RilletIntegrationConfig>) => {
      const next = { ...config, ...patch }
      setConfig(next)
      updateConfiguration(next)
    },
    [config, updateConfiguration]
  )

  const processCredentials = useCallback(
    (credentials: { apiKey: string; webhookSecret?: string }) => {
      setWasConnectionSuccessful(true)
      updateConfig({
        apiKey: credentials.apiKey,
        maskedApiKey: rilletConfiguration.maskedApiKey || '',
        webhookSecret: credentials.webhookSecret,
        maskedWebhookSecret: rilletConfiguration.maskedWebhookSecret
      })
      updateValidationState(true)
    },
    [
      updateConfig,
      updateValidationState,
      rilletConfiguration.maskedApiKey,
      rilletConfiguration.maskedWebhookSecret
    ]
  )

  const handleCredentialsChange = useCallback(
    (credentials: { apiKey?: string; webhookSecret?: string }) => {
      // Check if API key changed
      const apiKeyChanged =
        credentials.apiKey !== rilletConfiguration.maskedApiKey

      // Check if webhook secret changed
      const webhookSecretChanged =
        credentials.webhookSecret !== rilletConfiguration.maskedWebhookSecret

      // If API key changed, reset validation (require re-test)
      if (apiKeyChanged && wasConnectionSuccessful) {
        updateValidationState(false)
        setWasConnectionSuccessful(false)
      }

      // If webhook secret changed, enable save button
      if (webhookSecretChanged && !apiKeyChanged) {
        updateConfig({ webhookSecret: credentials.webhookSecret })
        updateValidationState(true)
      }
    },
    [
      wasConnectionSuccessful,
      updateValidationState,
      updateConfig,
      rilletConfiguration.maskedApiKey,
      rilletConfiguration.maskedWebhookSecret
    ]
  )

  const handleAutoLinkChange = useCallback(
    (next: boolean) => {
      updateConfig({ autoLinkCustomers: next })
      updateValidationState(true)
    },
    [updateConfig, updateValidationState]
  )

  const existingCredentials = useMemo(
    () => ({
      apiKey: rilletConfiguration.maskedApiKey,
      webhookSecret: rilletConfiguration.maskedWebhookSecret
    }),
    [rilletConfiguration.maskedApiKey, rilletConfiguration.maskedWebhookSecret]
  )

  const hasExistingApiKey = useMemo(() => {
    return !!rilletConfiguration.maskedApiKey
  }, [rilletConfiguration.maskedApiKey])

  const autoLinkPaused = Boolean(
    config.autoLinkCustomers &&
      rilletConfiguration.autoLinkSyncPermission &&
      rilletConfiguration.autoLinkSyncPermission !== 'ALLOW'
  )

  return (
    <Flex grow={1} width="100%">
      <Flex flexDirection="column" gap="20px" grow={1}>
        <RilletAuthenticationCard
          onConnectionSuccess={processCredentials}
          onCredentialsChange={handleCredentialsChange}
          existingCredentials={existingCredentials}
          hasExistingApiKey={hasExistingApiKey}
        />
        {hasExistingApiKey && (
          <AutoLinkCustomersCard
            service="Rillet"
            value={config.autoLinkCustomers}
            paused={autoLinkPaused}
            onChange={handleAutoLinkChange}
          />
        )}
      </Flex>
    </Flex>
  )
}
